import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { motion } from "framer-motion";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { logout, getPermissionContext } from "../../store/slices/authSlice";
import { setCurrentCompany } from "../../store/slices/companiesSlice";
import { fetchCompanies } from "../../store/slices/companiesSlice";
import { fetchPropertiesList } from "../../store/slices/propertiesSlice";
import { io, Socket } from "socket.io-client";
import NavigationSidebar from "../navigation/NavigationSidebar";
import NavigationSearch from "../navigation/NavigationSearch";
import NavigationBreadcrumbs from "../navigation/NavigationBreadcrumbs";
import cookieService from "../../services/cookieService";
// import ChatWidget from '../chat/ChatWidget';
import { ChevronLeftIcon, ChevronRightIcon, BuildingOfficeIcon, ChevronDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import SEOHead from "../SEO/SEOHead";
import { BRAND_NAME } from "../../config/brand";

const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem("dashboardSidebarCollapsed");
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  // Track if screen is smaller than Tailwind's lg breakpoint (1024px)
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(() => {
    try {
      return typeof window !== 'undefined'
        ? window.matchMedia('(max-width: 1023px)').matches
        : false;
    } catch {
      return false;
    }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, currentCompanyUser } = useSelector((state: RootState) => state.auth);
  const { companies, currentCompany } = useSelector((state: RootState) => state.companies);
  const socketRef = useRef<Socket | null>(null);
  const [searchTextVisible, setSearchTextVisible] = useState(false);
  const [searchOpenSignal, setSearchOpenSignal] = useState<number | null>(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; arrowTop: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);
  const userMenuDropdownRef = useRef<HTMLDivElement>(null);

  // Keep isSmallScreen in sync with window size
  useEffect(() => {
    try {
      const mql = window.matchMedia('(max-width: 1023px)');
      const onChange = (e: MediaQueryListEvent) => setIsSmallScreen(e.matches);
      // Set initial and subscribe to changes
      setIsSmallScreen(mql.matches);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    } catch {
      // no-op if matchMedia is unavailable
    }
  }, []);

  // Fetch companies on mount and auto-select first if none selected
  useEffect(() => {
    dispatch(fetchCompanies());
  }, [dispatch]);

  // Auto-select company from cookie or first available
  useEffect(() => {
    if (!currentCompany && companies.length > 0) {
      // Check if there's a saved company ID in cookie
      const savedCompanyId = cookieService.get('currentCompanyId');

      if (savedCompanyId) {
        // Find the saved company in the list
        const savedCompany = companies.find(c => c._id === savedCompanyId);
        if (savedCompany) {
          dispatch(setCurrentCompany(savedCompany));
        } else {
          // Saved company not found, select first available
          dispatch(setCurrentCompany(companies[0]));
        }
      } else {
        // No saved company, select first available
        dispatch(setCurrentCompany(companies[0]));
      }
    }
  }, [companies, currentCompany, dispatch]);

  // Note: Permission context is fetched when company is explicitly changed via handleCompanyChange

  // Preload lightweight properties list globally so command palette and selectors are instantly searchable.
  useEffect(() => {
    if (!currentCompany?._id) return;

    const hasPropertiesAccess =
      user?.role === "super_admin" ||
      (currentCompanyUser?.featurePermissions || []).includes("properties_read");

    if (!hasPropertiesAccess) return;

    dispatch(fetchPropertiesList({ companyId: currentCompany._id }));
  }, [
    dispatch,
    currentCompany?._id,
    user?.role,
    currentCompanyUser?.featurePermissions,
  ]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check company dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setShowCompanyDropdown(false);
      }

      // Check user menu - need to check both the button and the portal dropdown
      if (showUserMenu) {
        const isClickOnButton = userMenuButtonRef.current?.contains(target);
        const isClickOnDropdown = userMenuDropdownRef.current?.contains(target);

        if (!isClickOnButton && !isClickOnDropdown) {
          setShowUserMenu(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  useEffect(() => {
    // Initialize socket connection
    if (user) {
      const token = cookieService.get('token');
      socketRef.current = io(
        process.env.REACT_APP_SOCKET_URL || "http://localhost:5002",
        {
          auth: { token },
          transports: ["websocket", "polling"],
          withCredentials: true,
        }
      );

      socketRef.current.on("connect", () => {
        console.log("Socket connected");
      });

      socketRef.current.on("connect_error", (error) => {
        console.error("Socket connection error:", error);
      });

      socketRef.current.on("disconnect", () => {
        console.log("Socket disconnected");
      });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [user]);

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
    } catch (error) {
      // Handle logout error if needed
    }
    navigate("/");
  };

  const handleSidebarItemClick = () => {
    setSidebarOpen(false);
  };

  const toggleCollapse = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("dashboardSidebarCollapsed", JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  // Force expanded sidebar on small screens regardless of saved collapsed state
  const effectiveCollapsed = isSmallScreen ? false : sidebarCollapsed;

  const targetWidth = effectiveCollapsed ? 80 : 220;
  const showSearchVisible = !effectiveCollapsed;

  // Delay showing search text until fully expanded for 1s
  useEffect(() => {
    if (showSearchVisible) {
      const t = window.setTimeout(() => setSearchTextVisible(true), 350);
      return () => window.clearTimeout(t);
    }
    setSearchTextVisible(false);
  }, [showSearchVisible]);

  // Global hotkey at layout level ensures it works even when sidebar is collapsed
  useEffect(() => {
    const onHotkey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpenSignal(Date.now());
      }
    };
    window.addEventListener("keydown", onHotkey);
    return () => window.removeEventListener("keydown", onHotkey);
  }, [sidebarCollapsed]);

  // Reset the open signal shortly after firing so remounts don't reopen the modal
  useEffect(() => {
    if (searchOpenSignal !== null) {
      const t = window.setTimeout(() => setSearchOpenSignal(null), 250);
      return () => window.clearTimeout(t);
    }
  }, [searchOpenSignal]);

  return (
    <>
      <SEOHead
      title={`Dashboard - ${BRAND_NAME}`}
      description={`${BRAND_NAME} dashboard to manage properties, tenants, rent collection, and maintenance.`}
      ogTitle={`Dashboard - ${BRAND_NAME}`}
      ogDescription={`${BRAND_NAME} dashboard to manage properties, tenants, rent collection, and maintenance.`}
      noindex
      />
      <div className="h-screen bg-gray-50 flex overflow-hidden dashboard-layout">
        {/* Sidebar */}
        <motion.aside
          animate={{ width: targetWidth }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          className={`dashboard_navbar fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transform ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:h-full lg:flex lg:flex-col overflow-x-hidden`}
          style={{ width: targetWidth }}
        >
          <div
            className={`dashboard_navbar_header min-h-[64px] flex items-center justify-between px-2 bg-white border-b border-gray-200 gap-[5px]`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center">
                <img src="/assets/rentBhaiLogo.svg" alt={`${BRAND_NAME} Logo`} className="w-10 h-10" />
              </div>
              {!effectiveCollapsed && (
                <h1 className="text-purple-600 text-lg font-bold tracking-tight whitespace-nowrap">
                  {BRAND_NAME}
                </h1>
              )}
            </div>
            <button
              type="button"
              onClick={toggleCollapse}
              className={`hidden lg:inline-flex items-center justify-center rounded-lg transition-all group ${
                effectiveCollapsed
                  ? "w-8 h-8 text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                  : "w-8 h-8 text-gray-600 hover:text-purple-600 hover:bg-purple-50"
              }`}
              aria-label={
                effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
              aria-pressed={effectiveCollapsed}
              title={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <div className="relative">
                {effectiveCollapsed ? (
                  <ChevronRightIcon className="w-6 h-6 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                ) : (
                  <ChevronLeftIcon className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform flex-shrink-0" />
                )}
              </div>
            </button>
          </div>

          {/* Navigation with proper spacing */}
          <div className="flex flex-col h-full min-h-0">
            {/* Navigation Search */}
            <div className="px-2 mt-4">
              {/* Reserve space to avoid vertical jitter; container height accommodates full search */}
              <div
                className={`h-12 flex items-center ${
                  effectiveCollapsed ? "border-y border-gray-200/70" : ""
                }`}
              >
                <div className="w-full">
                  {effectiveCollapsed ? (
                    <div className="flex justify-center">
                      <NavigationSearch
                        openSignal={searchOpenSignal ?? undefined}
                        compact
                        onItemClick={handleSidebarItemClick}
                      />
                    </div>
                  ) : (
                    <NavigationSearch
                      openSignal={searchOpenSignal ?? undefined}
                      onItemClick={handleSidebarItemClick}
                      showText={searchTextVisible}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Dynamic Navigation */}
            <NavigationSidebar
              collapsed={effectiveCollapsed}
              onItemClick={handleSidebarItemClick}
            />

            {/* User section - fixed at bottom */}
            <div className="flex-shrink-0 p-3 border-t border-gray-200 bg-white">
              {effectiveCollapsed ? (
                /* Collapsed view - Just avatar with dropdown */
                <div className="relative">
                  <button
                    ref={userMenuButtonRef}
                    onClick={() => {
                      if (!showUserMenu && userMenuButtonRef.current) {
                        const rect = userMenuButtonRef.current.getBoundingClientRect();
                        const dropdownHeight = 480; // max height
                        const buttonCenterY = rect.top + (rect.height / 2);

                        // Calculate vertical position to keep dropdown in viewport
                        let top = buttonCenterY;
                        const bottomOverflow = top + (dropdownHeight / 2) - window.innerHeight;

                        if (bottomOverflow > 0) {
                          // Adjust if going below viewport
                          top = top - bottomOverflow - 20; // 20px padding from bottom
                        }

                        // Make sure it doesn't go above viewport
                        const topMin = dropdownHeight / 2 + 20; // 20px padding from top
                        if (top < topMin) {
                          top = topMin;
                        }

                        // Calculate arrow position (where button center is relative to dropdown top)
                        const arrowTop = buttonCenterY - top + (dropdownHeight / 2);

                        setDropdownPosition({
                          top: top,
                          left: rect.right + 12,
                          arrowTop: arrowTop,
                        });
                      }
                      setShowUserMenu(!showUserMenu);
                    }}
                    className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                    aria-label="User menu"
                  >
                    <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 group-hover:scale-105 transition-transform shadow-md">
                      {user?.firstName?.charAt(0)}
                      {user?.lastName?.charAt(0)}
                    </div>
                  </button>

                  {/* Dropdown menu for collapsed sidebar - using portal */}
                  {showUserMenu && dropdownPosition && ReactDOM.createPortal(
                    <div
                      ref={userMenuDropdownRef}
                      className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[9999]"
                      style={{
                        top: `${dropdownPosition.top + 20}px`,
                        left: `${dropdownPosition.left - 20}px`,
                        width: '256px',
                        maxHeight: '480px',
                        overflowY: 'auto'
                      }}
                    >
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md">
                            {user?.firstName?.charAt(0)}
                            {user?.lastName?.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {user?.firstName} {user?.lastName}
                            </p>
                            <p className="text-xs text-gray-500 capitalize truncate">
                              {user?.subRole?.replace("_", " ")}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Company Info */}
                      {currentCompany && (
                        <div className="px-4 py-2 border-b border-gray-100">
                          <div className="flex items-center gap-2 text-[13px] text-gray-600">
                            <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
                            <span className="truncate font-medium">{currentCompany.name}</span>
                          </div>
                        </div>
                      )}

                      {/* Menu Items */}
                      <div className="py-1">
                        <button
                          onClick={() => {
                            navigate('/dashboard/profile');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                        >
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Profile Settings
                        </button>

                        {companies.length > 1 && (
                          <button
                            onClick={() => {
                              setShowCompanyDropdown(!showCompanyDropdown);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                          >
                            <BuildingOfficeIcon className="w-4 h-4 text-gray-500" />
                            Switch Company
                            <ChevronDownIcon className={`w-4 h-4 ml-auto text-gray-400 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} />
                          </button>
                        )}

                        {/* Company dropdown inside user menu */}
                        {showCompanyDropdown && companies.length > 1 && (
                          <div className="mx-2 my-1 bg-gray-50 rounded-md max-h-40 overflow-y-auto">
                            {companies.map((company) => (
                              <button
                                key={company._id}
                                onClick={() => {
                                  dispatch(setCurrentCompany(company));
                                  setShowCompanyDropdown(false);
                                  setShowUserMenu(false);
                                  navigate('/dashboard');
                                  setTimeout(() => {
                                    dispatch(getPermissionContext());
                                  }, 100);
                                }}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-white rounded flex items-center justify-between transition-colors"
                              >
                                <span className="truncate">{company.name}</span>
                                {currentCompany?._id === company._id && (
                                  <CheckIcon className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="border-t border-gray-100 pt-1">
                        <button
                          onClick={() => {
                            handleLogout();
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign out
                        </button>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              ) : (
                /* Expanded view - Full layout */
                <div>
                  {/* User Profile Button */}
                  <button
                    onClick={() => navigate('/dashboard/profile')}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-all mb-3 group"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 group-hover:scale-105 transition-transform shadow-md">
                      {user?.firstName?.charAt(0)}
                      {user?.lastName?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-purple-600 transition-colors">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-gray-500 capitalize truncate">
                        {user?.subRole?.replace("_", " ")}
                      </p>
                    </div>
                    <svg className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Company Switcher */}
                  {companies.length > 1 ? (
                    <div className="relative mb-2" ref={dropdownRef}>
                      <button
                        onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center min-w-0 gap-2">
                          <BuildingOfficeIcon className="w-4 h-4 flex-shrink-0 text-gray-500" />
                          <span className="truncate font-medium text-xs">
                            {currentCompany ? currentCompany.name : 'Select Company'}
                          </span>
                        </div>
                        <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} />
                      </button>

                      {showCompanyDropdown && companies.length > 0 && (
                        <div className="absolute bottom-full mb-2 left-0 right-0 bg-white rounded-lg shadow-xl border border-gray-200 max-h-48 overflow-y-auto z-50 py-1">
                          {companies.map((company) => (
                            <button
                              key={company._id}
                              onClick={() => {
                                dispatch(setCurrentCompany(company));
                                setShowCompanyDropdown(false);
                                navigate('/dashboard');
                                setTimeout(() => {
                                  dispatch(getPermissionContext());
                                }, 100);
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 flex items-center justify-between group transition-colors"
                            >
                              <span className="truncate text-gray-700 group-hover:text-purple-700 font-medium">{company.name}</span>
                              {currentCompany?._id === company._id && (
                                <CheckIcon className="w-4 h-4 text-purple-600 flex-shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : currentCompany ? (
                    /* Single company display */
                    <div className="px-3 py-2 mb-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 text-[13px] text-gray-600">
                        <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                        <span className="truncate font-medium">{currentCompany.name}</span>
                      </div>
                    </div>
                  ) : null}

                  {/* Sign Out Button */}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all flex items-center gap-2 font-medium group"
                  >
                    <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.aside>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile header */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between h-16 bg-white shadow px-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 text-gray-600 hover:text-gray-900"
                aria-label="Open sidebar"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900">{BRAND_NAME}</h1>
              <div className="w-8"></div>
            </div>
          </div>

          {/* Page content */}
          <main className="flex-1 overflow-x-hidden overflow-y-auto">
            <div className="p-6">
              <NavigationBreadcrumbs />
              <div className="-m-6">
                {/* Key Outlet by path+search+refresh state to force remount on sidebar refresh */}
                <Outlet key={`${location.pathname}${location.search}${(location.state as any)?.__refresh ?? ''}`} />
              </div>
            </div>
          </main>
        </div>

        {/* Chat Widget */}
        {/* {user && <ChatWidget socket={socketRef.current} />} */}
      </div>
    </>
  );
};

export default DashboardLayout;
